import { useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from '@components/ui/Modal'
import Input from '@components/ui/Input'
import Button from '@components/ui/Button'
import { Typography, Alert } from '@mui/material'
import { AuthContext } from '@contexts/AuthContext'

export default function ForcePasswordChangeModal({ isOpen, onPasswordChanged }) {
  const { logout } = useContext(AuthContext)
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleLogout = () => {
    console.log('🚪 Botão Sair clicado - executando logout...')
    logout()
    console.log('➡️ Navegando para /login')
    navigate('/login')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validações
    if (formData.new_password !== formData.confirm_password) {
      setError('As senhas não coincidem')
      return
    }

    if (formData.new_password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres')
      return
    }

    setLoading(true)

    try {
      // SEGURANÇA: Token agora em sessionStorage (após correções de segurança)
      const token = sessionStorage.getItem('bravo-tax-token') || localStorage.getItem('bravo-tax-token')
      console.log('🔑 Token presente:', !!token)
      console.log('📤 Enviando requisição para /api/bravosim/v1/users/change_password/')

      const response = await fetch('/api/bravosim/v1/users/change_password/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          old_password: formData.old_password,
          new_password: formData.new_password
        })
      })

      console.log('📥 Response status:', response.status)

      let data
      try {
        data = await response.json()
        console.log('📦 Response data:', data)
      } catch (parseError) {
        console.error('❌ Erro ao fazer parse da resposta:', parseError)
        setError('Erro ao processar resposta do servidor')
        return
      }

      if (!response.ok) {
        console.error('❌ Erro na resposta:', data)

        // Se for 401, significa que o token é inválido
        if (response.status === 401) {
          setError('Sessão inválida. Por favor, faça login novamente.')
          setTimeout(() => {
            handleLogout()
          }, 2000)
          return
        }

        // Trata arrays de erros (ex: validação de senha)
        if (Array.isArray(data.error)) {
          setError(data.error.join('. '))
        } else if (data.old_password) {
          setError('Senha atual incorreta')
        } else if (data.new_password) {
          const errors = Array.isArray(data.new_password) ? data.new_password : [data.new_password]
          setError(errors.join('. '))
        } else {
          setError(data.error || data.detail || 'Erro ao alterar senha')
        }
        return
      }

      console.log('✅ Senha alterada com sucesso!')
      // Sucesso - chama callback para limpar o flag e recarregar
      onPasswordChanged()
    } catch (err) {
      console.error('❌ Erro na requisição:', err)
      setError('Erro ao conectar com o servidor. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      title="Alteração de Senha Obrigatória"
      onClose={() => {}} // Não permite fechar
      closeOnOutsideClick={false}
      showCloseButton={false}
    >
      <div className="space-y-4">
        <Alert severity="warning">
          Você precisa alterar sua senha antes de continuar.
        </Alert>

        <Typography variant="body2" color="text.secondary">
          Por segurança, você deve criar uma nova senha forte.
        </Typography>

        <Alert severity="info">
          <Typography variant="body2">
            A senha deve ter:
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
              <li>Mínimo de 8 caracteres</li>
              <li>Não pode ser muito similar aos seus dados pessoais</li>
              <li>Não pode ser uma senha muito comum</li>
              <li>Não pode ser totalmente numérica</li>
            </ul>
          </Typography>
        </Alert>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Senha Atual"
            name="old_password"
            type="password"
            value={formData.old_password}
            onChange={handleChange}
            required
            autoFocus
          />

          <Input
            label="Nova Senha"
            name="new_password"
            type="password"
            value={formData.new_password}
            onChange={handleChange}
            required
            placeholder="Mínimo 8 caracteres"
          />

          <Input
            label="Confirmar Nova Senha"
            name="confirm_password"
            type="password"
            value={formData.confirm_password}
            onChange={handleChange}
            required
          />

          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          <div className="flex justify-between items-center gap-3">
            <button
              type="button"
              onClick={handleLogout}
              disabled={loading}
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 underline text-sm disabled:opacity-50"
            >
              Sair e voltar ao login
            </button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
